import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, Scroll} from '@angular/router';
import {combineLatest, delay, distinctUntilChanged, filter, Observable, startWith, tap} from 'rxjs';

import { AppRepository } from '~shared/store';
import ProjectsRepository from "~modules/project/store/projects/projects.repository";
import {Project} from "~shared/types";
import { ExternalUser, InternalUser} from "~modules/auth/types/user.types";
import {UserType} from "~shared/enums";
import { RouteUtilsService } from '~shared/services/route-utils.service';

import { AuthService } from '../../services/auth.service';


@Component({
	templateUrl: './wrapper.component.html',
})
export class WrapperComponent implements OnInit {
	public backgroundLoading$: Observable<boolean>;
	public loading: boolean;
	public menuItems = [];

	public disableNavigation = false;

	constructor(
		private readonly authService: AuthService,
		private readonly appRepository: AppRepository,
		private readonly projectsRepository: ProjectsRepository,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly routeUtilsService: RouteUtilsService,
	) {}

	ngOnInit() {
		// Compute immediately too: on a cold load the initial NavigationEnd can
		// fire before this subscription exists, leaving the header visible on
		// no-chrome (public) routes until the next navigation.
		this.disableNavigation = !!this.route.snapshot.firstChild?.data?.disableNavigation;
		this.authService.loading$
			.pipe(distinctUntilChanged(), delay(0))
			.subscribe((loading) => loading ? this.appRepository.incrementBackgroundLoading() : this.appRepository.decrementBackgroundLoading());
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe(() => {
				this.disableNavigation = !!this.route.snapshot.firstChild?.data?.disableNavigation;
			});


 		// Defer the binding past the current CD cycle. Without this, a route
		// activation triggered from an account switch can run
		// ProjectsComponent.ngOnInit (which calls incrementLoading()) inside
		// the same CD pass that the async pipe on this binding has already
		// read — flipping backgroundLoading$ from true to false mid-cycle and
		// throwing NG0100.
		this.backgroundLoading$ = this.appRepository.backgroundLoading$.pipe(
			distinctUntilChanged(),
			delay(0),
		);

		this.setHeaderMenu();
		this.changeAccountListener();
	}


	setHeaderMenu() {
		combineLatest([
			this.authService.currentUser$,
			this.projectsRepository.projects$,
			this.router.events.pipe(
				startWith(null),
				filter((router) => !router || router instanceof Scroll)
			)
		])
			.pipe(
				tap(([user, projects, event]: [InternalUser | ExternalUser, Project[], Scroll]) => {
					const isTermsPage = this.router.url.includes('algemene_voorwaarden');

					if (!user || !user.registered || isTermsPage) {
						return;
					}

					this.menuItems = [
						{
							icon: 'home',
							to: '/projects'
						},
						{
							icon: 'wood',
							to: '/materials',
						},
					];
					const isEditor = this.router.url.includes('configurator');

					if (!isEditor) {
						const activeProject  = this.projectsRepository.queryActiveProject();
						// The header link goes back to where the user last was in this project's configurator
						const lastConfigurator = activeProject ? this.routeUtilsService.lastConfiguratorPlace(activeProject.id) : null;

						const editMenu = {
							icon: 'configurator',
							appTooltip: !!activeProject ? `Terug naar project ${activeProject.name || activeProject.id}` : 'Start nieuw project',
							to: lastConfigurator?.path ?? (!!activeProject ? `/projects/${activeProject.id}/configurator` : '/projects/create'),
						    ...( user.userType === UserType.EXTERNAL && !activeProject ? {queryParams: {autoCreate: true }} : {}),
						    ...( lastConfigurator ? {queryParams: lastConfigurator.queryParams} : {})
						};
						this.menuItems.splice(1, 0, editMenu);
					}
				}))
			.subscribe();
	}

	changeAccountListener() {
		combineLatest([
			this.authService.accountIdOverwrite$,
			this.authService.currentUser$,
		])
			.pipe(
				distinctUntilChanged(),
			)
			.subscribe(([accountId, user]) => {
				const project = this.projectsRepository.queryActiveProject();
				if (
					(project &&
					project?.accountId !== accountId) &&
					!!user?.account &&
					user.userType === UserType.INTERNAL
				) {
					this.router.navigate(['/', 'projects']);
				} else if (
					project &&
					project.accountId !== accountId &&
					!user
				) {
					this.authService.overwriteAccountId(
						project.accountId,
						true
					);
				}
			});
	}
}
